import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useState, useEffect, useLayoutEffect } from "react";
import NavBar from "../components/NavBar";
import SideBar from "../components/SideBar";
import BottomNavigation from "../components/BottomNavigation";
import GodModeIndicator from "../components/GodModeIndicator";
import AndroidAppModal from "../components/AndroidAppModal";
import IosAppModal from "../components/IosAppModal";
import ZohoWidget from "../components/ZohoWidget";
import ZipCodeModal from "../components/ZipCodeModal";
import { useUserContext } from "../context/UserContext";
import { useZohoWidget } from "../hooks/useZohoWidget";
import { isFreeTier, isUserSetUp } from "../utils/userUtils";
import {
  isDealOnboardingAllowedPath,
  isDealOnboardingPath,
  needsDealOnboarding,
} from "../utils/dealOnboarding";
import { FavoritesProvider } from "../context/FavoritesContext";
import { logOut } from "../services/AuthService";
import { isHomeFeedPath } from "../config/publicDeals";
import { isFreeAccountShellPath, isFreePublicFeedPath } from "../components/AppRouter.utils";
import FreeAccountShell from "../components/home-feed/FreeAccountShell";

export default function AppRoot({ authed }: { authed: boolean }) {
  const { user } = useUserContext();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Every page opens at the top. Pages scroll inside the shell's containers
  // (marked data-route-scroll), not the window, so reset both on navigation.
  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    document.querySelectorAll<HTMLElement>("[data-route-scroll]").forEach((el) => {
      el.scrollTo({ top: 0, left: 0, behavior: "instant" });
    });
  }, [location.pathname]);
  const hasRetailers = !!(user?.retailers && user.retailers.length > 0);

  // Zip code modal state
  const [showZipCodeModal, setShowZipCodeModal] = useState(false);

  // Zoho widget configuration for global use (when not on ProductsPage)
  // Also include leads page and chat page for proper virtualized list layout
  const isProductsPage =
    location.pathname.includes("/products") || 
    location.pathname.includes("/leads") ||
    location.pathname === "/hot-deals" ||
    location.pathname === "/hawk-finds" ||
    location.pathname === "/community-successes" ||
    location.pathname === "/featured-deals" ||
    location.pathname === "/hawk-ai" ||
    location.pathname === "/" ||
    (!user && isHomeFeedPath(location.pathname));
  const zohoWidgetProps = useZohoWidget({
    hasBottomNav: isUserSetUp(user) || isFreeTier(user),
  });

  // Debug: Check page detection (can be removed in production)
  // console.log("AppRoot - Page detection:", {
  //   pathname: location.pathname,
  //   isProductsPage,
  //   shouldShowGlobalWidget: !isProductsPage && zohoWidgetProps.shouldShowWidget,
  // });

  const handleMenuClick = () => {
    setIsSidebarOpen(true);
  };

  const handleSidebarClose = () => {
    setIsSidebarOpen(false);
  };

  useEffect(() => {
    if (!user || user.zipCode) return;
    if (isDealOnboardingPath(location.pathname) || location.pathname === "/setup") return;
    if (user.dealIntent === "online") return;
    if (hasRetailers || user.dealIntent === "instore") {
      setShowZipCodeModal(true);
    }
  }, [user, hasRetailers, location.pathname]);

  if (authed && !user) {
    if (location.pathname !== "/get-started") {
      return <Navigate to="/get-started" />;
    }
  }

  if (authed && user) {
    // Check if user is banned
    if (user.banned) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-dark-950">
          <div className="max-w-md w-full mx-4 bg-white dark:bg-dark-800 rounded-lg shadow-lg p-8 text-center">
            <div className="mb-6">
              <div className="mx-auto w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mb-4">
                <svg
                  className="w-8 h-8 text-red-600 dark:text-red-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
                  />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Your account is suspended
              </h1>
              <p className="text-gray-600 dark:text-dark-300">
                Your account has been suspended. If you believe this is an
                error, please contact support.
              </p>
            </div>
            <button
              onPointerDown={() => {
                logOut();
                window.location.href = "/get-started";
              }}
              className="w-full px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors font-medium"
            >
              Log Out
            </button>
          </div>
        </div>
      );
    }

    if (!user.verified) {
      if (location.pathname !== "/verify") {
        return <Navigate to="/verify" />;
      } else {
        return (
          <div className="h-[100svh] flex flex-col min-w-0 overflow-hidden">
            <main data-route-scroll className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
              <div className="max-w-8xl mx-auto px-4 sm:px-0">
                <FavoritesProvider>
                  <Outlet />
                </FavoritesProvider>
              </div>
            </main>
          </div>
        );
      }
    }

    if (needsDealOnboarding(user) && !isDealOnboardingAllowedPath(location.pathname)) {
      return <Navigate to="/onboarding" replace />;
    }

    if (isDealOnboardingPath(location.pathname) || (location.pathname === "/setup" && !hasRetailers)) {
      // #root is clipped to the viewport, so the wrapper must be viewport-high
      // for <main> to become the scroll container; otherwise tall content
      // (the paywall's plan list) is simply cut off.
      return (
        <div className="h-[100svh] flex flex-col min-w-0 overflow-hidden">
          <main data-route-scroll className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
            <FavoritesProvider>
              <Outlet />
            </FavoritesProvider>
          </main>
        </div>
      );
    }
  }

  // Signed-in free accounts live on the public deals site, like pinto: the feed pages
  // render exactly as for a guest (no members chrome), and the account pages they keep
  // get the site header with "Back to deals". Members-only paths are redirected to the
  // public store page by AppRouter, so anything else here is a transient frame.
  if (authed && user && isFreeTier(user)) {
    if (isFreePublicFeedPath(location.pathname)) {
      return (
        <div data-route-scroll className="relative h-full flex flex-col overflow-y-auto overscroll-y-none scroll-smooth">
          <GodModeIndicator />
          <div className="flex-1">
            <FavoritesProvider>
              <Outlet />
            </FavoritesProvider>
          </div>
        </div>
      );
    }
    if (isFreeAccountShellPath(location.pathname)) {
      return (
        <>
          <GodModeIndicator />
          <FreeAccountShell>
            <FavoritesProvider>
              <Outlet />
            </FavoritesProvider>
          </FreeAccountShell>
        </>
      );
    }
  }

  if (authed && user && !hasRetailers && !isFreeTier(user)) {
    const isAdminOnAdminPage = user.admin && location.pathname.startsWith("/admin");
    
    if (isAdminOnAdminPage) {
      // Admin users can access admin pages without setup - fall through to main layout
    } else if (
      location.pathname !== "/setup" &&
      location.pathname !== "/success" &&
      location.pathname !== "/contact-us"
    ) {
      return <Navigate to="/setup" />;
    } else {
      return (
        <div className="h-[100svh] flex overflow-hidden">
          {user && <SideBar isOpen={isSidebarOpen} onClose={handleSidebarClose} />}
          <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
            <NavBar onMenuClick={handleMenuClick} />
            <main className="flex-1 min-h-0 overflow-hidden">
              <FavoritesProvider>
                <Outlet />
              </FavoritesProvider>
            </main>
          </div>
        </div>
      );
    }
  }

  // For unauthenticated users (landing page), don't render NavBar since LandingPage has its own navigation
  if (!authed) {
    return (
      <div data-route-scroll className="relative h-full flex flex-col overflow-y-auto overscroll-y-none scroll-smooth">
        <NavBar onMenuClick={handleMenuClick} hideMenuOnMobile={true} />
        <div className="flex-1">
          <FavoritesProvider>
            <Outlet />
          </FavoritesProvider>
        </div>
      </div>
    );
  }

  // AdminPage owns its admin rail and mobile tab switcher, so it skips the
  // navbar; the app sidebar stays on desktop (it is off-canvas on mobile).
  if (user?.admin && location.pathname.startsWith("/admin")) {
    return (
      <div className="h-full flex overflow-hidden bg-gray-50 dark:bg-dark-950">
        <GodModeIndicator />
        <SideBar isOpen={isSidebarOpen} onClose={handleSidebarClose} />
        <div data-route-scroll className="flex-1 min-w-0 overflow-y-auto">
          <FavoritesProvider>
            <Outlet />
          </FavoritesProvider>
        </div>
      </div>
    );
  }

  return (
    // h-full (not h-screen): #root is clipped to the visible viewport, and on
    // iOS 100vh is the taller toolbar-collapsed viewport, so an h-screen shell
    // would hide its bottom rows behind the browser toolbar.
    <div className="h-full flex overflow-hidden bg-gray-50 dark:bg-dark-950">
      <GodModeIndicator />
      {/* Show sidebar only on desktop */}
      {user && <SideBar isOpen={isSidebarOpen} onClose={handleSidebarClose} />}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Modify NavBar to hide hamburger menu on mobile when bottom nav is active */}
        <NavBar
          onMenuClick={handleMenuClick}
          hideMenuOnMobile={isUserSetUp(user) || isFreeTier(user)}
        />
        <main
          data-route-scroll
          className={`flex-1 bg-gray-50 dark:bg-dark-950 ${
            isProductsPage
              ? "overflow-hidden flex flex-col"
              : "overflow-y-auto overflow-x-hidden pb-20 md:pb-0"
          }`}
        >
          <div
            className={`${
              isProductsPage
                ? "flex-1 flex flex-col min-h-0 w-full"
                : "max-w-8xl mx-auto px-0 sm:px-0 mb-20 md:mb-0"
            }`}
          >
            <FavoritesProvider>
              <Outlet />
            </FavoritesProvider>
          </div>
        </main>
      </div>
      {/* Show bottom navigation on mobile for setup-complete and free users */}
      {(isUserSetUp(user) || isFreeTier(user)) && <BottomNavigation />}

      {/* Global Zoho Widget - only show when NOT on ProductsPage to avoid conflicts */}
      {!isProductsPage && zohoWidgetProps.shouldShowWidget && (
        <ZohoWidget
          showAboveGoUpButton={false}
          isMobile={zohoWidgetProps.isMobile}
          hasBottomNav={zohoWidgetProps.hasBottomNav}
          instanceId="global"
        />
      )}

      {/* Zip Code Modal */}
      <ZipCodeModal
        isOpen={showZipCodeModal}
        onClose={() => setShowZipCodeModal(false)}
        currentZipCode={user?.zipCode || ""}
        title="Set Zip Code"
        description="Enter your zip code to search nearby stores."
      />

      {/* App Download Modals */}
      {authed && hasRetailers && (
        <>
          <AndroidAppModal />
          <IosAppModal />
        </>
      )}
    </div>
  );
}
